/**
 * @file chat-conversation.schema.ts
 * @description Define o schema Zod para a entidade 'Conversation'.
 * Cada documento nesta coleção representa uma única conversa (direct ou group).
 */
import { z } from 'zod';
import { MessageSummarySchema } from './chat-message.schema';

// --- Helper Schemas & Enums ---

const ConversationTypeEnum = z.enum(['direct', 'group']);

const ParticipantStateEntrySchema = z.object({
  lastReadSeq: z.number().int().nonnegative().default(0),
  lastReadAt: z.date().optional(),
  // Última vez que este participante foi notificado externamente (e-mail/SMS/push)
  // sobre novas mensagens. Alimenta o debounce das notificações transacionais.
  lastNotifiedAt: z.date().optional(),
});


// --- Main Conversation Schema ---

const BaseConversationSchema = z.object({
  id: z.string(),
  /**
   * Quem está na conversa. Na conversa de canal (A144): a paciente e quem
   * atende o canal, mantido em dia quando a marca muda os membros — pode ficar
   * só a paciente, se o canal ficar sem ninguém. Por isso o mínimo é 1.
   */
  participantIds: z.array(z.string()).min(1),
  /** A conversa é com um canal (A144), não com uma pessoa. */
  canalId: z.string().optional(),
  /** Na conversa de canal, quem é a paciente (os demais participantes atendem). */
  pacienteId: z.string().optional(),
  /**
   * A conversa direta antiga que esta virou, na migração para os canais
   * (A146): o formato de antes, para reverter por escrita. Ausente em toda
   * conversa que nasceu de canal.
   */
  migradaDe: z
    .object({
      type: z.enum(['direct', 'group']),
      name: z.string().nullable().optional(),
      /** O canal de antes: nulo na conversa direta ou no grupo; preenchido na conversa de canal que só foi renumerada. */
      canalId: z.string().nullable().optional(),
      pacienteId: z.string().nullable().optional(),
      participantIds: z.array(z.string()),
      seq: z.number().int().nonnegative(),
      totalMessages: z.number().int().nonnegative(),
      participantState: z.record(z.any()).optional(),
      lastMessage: z.any().optional(),
      /** ISO, e não data: um Timestamp aninhado não passa pela conversão de datas da leitura. */
      migradaEm: z.string(),
    })
    .optional(),
  /** Conversa antiga cujas mensagens foram para a do canal (A146); vai junto com `deletedAt`. */
  migradaPara: z.string().optional(),
  /**
   * A marca dona da conversa (A030).
   *
   * Antes disso a conversa não tinha dono: a marca era inferida das
   * participantes na criação e nunca mais consultada — e a inferência
   * dependia de `partnerId`, um escalar que assume uma marca por pessoa.
   *
   * Nasce OPCIONAL de propósito. Toda conversa gravada antes do backfill está
   * sem o campo, e `BaseRepository.findMany` lança no primeiro documento
   * inválido: exigir agora derrubaria a listagem inteira. Vira obrigatório
   * num deploy posterior, quando o relatório da migração zerar.
   */
  brandId: z.string().optional(),
  lastMessage: MessageSummarySchema.optional(),
  // Novo modelo seq / leitura derivada
  seq: z.number().int().nonnegative().default(0),
  totalMessages: z.number().int().nonnegative().default(0),
  participantState: z.record(ParticipantStateEntrySchema).default({}),
  // Campo legado mantido para compatibilidade frontend até migração completa
  version: z.number().int().nonnegative().default(1),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().default(null),
});

const DirectConversationSchema = BaseConversationSchema.extend({
  type: z.literal('direct'),
});

const GroupConversationSchema = BaseConversationSchema.extend({
  type: z.literal('group'),
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  logoUrl: z.string().url().nullable().optional(),
});

const ConversationSchema = z.discriminatedUnion('type', [
  DirectConversationSchema,
  GroupConversationSchema,
]);


// --- Inferred TypeScript Types ---

type Conversation = z.infer<typeof ConversationSchema>;
type ConversationType = z.infer<typeof ConversationTypeEnum>;


// --- Public Exports ---

export { ConversationSchema, ConversationTypeEnum, ParticipantStateEntrySchema };
export type { Conversation, ConversationType };